/* specs/06-cart-drawer.md. Exposes window.VelourCart.addItem() for product cards,
   the product page and the mega-menu's featured "Add to bag" to share one path. */

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.cart-drawer__panel');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.addEventListener('keydown', (event) => {
      if (event.code?.toUpperCase() === 'ESCAPE') this.close();
    });

    /* One delegated click handler. The drawer's contents are replaced after every add,
       quantity change or removal, so listeners bound directly to the overlay or the
       close button were lost after the first refresh and the drawer could no longer be
       closed. */
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-overlay]') || event.target.closest('[data-cart-close]')) {
        this.close();
        return;
      }
      const step = event.target.closest('[data-cart-qty-step]');
      if (step) {
        const input = step.parentElement.querySelector('.quantity__input');
        const next = Math.max(0, parseInt(input.value, 10) + parseInt(step.dataset.cartQtyStep, 10));
        this.updateLine(step.dataset.line, next);
        return;
      }
      const remove = event.target.closest('[data-cart-remove]');
      if (remove) this.updateLine(remove.dataset.line, 0);
    });

    this.addEventListener(
      'change',
      debounce((event) => {
        if (event.target.matches('.quantity__input')) {
          this.updateLine(event.target.dataset.line, Math.max(0, parseInt(event.target.value, 10) || 0));
        }
        if (event.target.matches('[data-gift-toggle]')) {
          const noteWrap = this.querySelector('[data-gift-note]');
          const noteText = this.querySelector('[data-gift-note-text]');
          if (noteWrap) noteWrap.hidden = !event.target.checked;
          this.updateAttributes({
            gift: event.target.checked ? 'yes' : '',
            gift_note: event.target.checked ? noteText?.value || '' : '',
          });
        }
      }, ON_CHANGE_DEBOUNCE_TIMER)
    );

    this.addEventListener(
      'input',
      debounce((event) => {
        if (event.target.matches('[data-gift-note-text]')) {
          this.updateAttributes({ gift: 'yes', gift_note: event.target.value });
        }
      }, ON_CHANGE_DEBOUNCE_TIMER)
    );
  }

  open() {
    this.panel = this.querySelector('.cart-drawer__panel');
    this.openedBy = document.activeElement;
    this.removeAttribute('hidden');
    requestAnimationFrame(() => this.setAttribute('data-open', ''));
    document.body.style.overflow = 'hidden';
    trapFocus(this.panel, this.querySelector('[data-cart-close]'));
  }

  close() {
    this.removeAttribute('data-open');
    removeTrapFocus(this.openedBy);
    document.body.style.overflow = '';
    const delay = this.reducedMotion ? 0 : 320;
    setTimeout(() => {
      if (!this.hasAttribute('data-open')) this.setAttribute('hidden', '');
    }, delay);
  }

  updateLine(line, quantity) {
    fetch(window.routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify({ line, quantity, sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname }),
    })
      .then((response) => response.json())
      .then((state) => this.renderFromSections(state.sections))
      .catch((error) => console.error(error));
  }

  updateAttributes(attributes) {
    fetch(window.routes.cart_update_url, { ...fetchConfig(), body: JSON.stringify({ attributes }) }).catch((error) =>
      console.error(error)
    );
  }

  renderFromSections(sections) {
    if (sections['cart-drawer']) {
      const wasOpen = this.hasAttribute('data-open');
      const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const newDrawer = html.querySelector('cart-drawer');
      if (newDrawer) {
        this.innerHTML = newDrawer.innerHTML;
        this.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
        this.panel = this.querySelector('.cart-drawer__panel');
        if (wasOpen) {
          this.removeAttribute('hidden');
          this.setAttribute('data-open', '');
        }
        VelourCart.announceVariants(newDrawer);
      }
    }
    if (sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      const html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const newContent = html.querySelector('.shopify-section');
      if (bubble && newContent) bubble.innerHTML = newContent.innerHTML;
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

window.VelourCart = {
  addItem(variantId, quantity, options) {
    quantity = quantity || 1;
    options = options || {};
    const openDrawer = options.openDrawer !== false;
    const item = { id: variantId, quantity: quantity };
    if (options.sellingPlanId) item.selling_plan = options.sellingPlanId;
    if (options.properties) item.properties = options.properties;

    return fetch(`${window.routes.cart_add_url}.js`, {
      ...fetchConfig(),
      body: JSON.stringify({ items: [item] }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status) throw new Error(data.description || 'Could not add to bag');
        return VelourCart.refreshDrawer(openDrawer);
      });
  },

  refreshDrawer(openDrawer) {
    return fetch(`${window.routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const newDrawer = html.querySelector('cart-drawer');
        const drawer = document.querySelector('cart-drawer');
        if (drawer && newDrawer) {
          drawer.innerHTML = newDrawer.innerHTML;
          drawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
          VelourCart.announceVariants(newDrawer);
          if (openDrawer) drawer.open();
        }
        return VelourCart.refreshHeaderBubble();
      });
  },

  refreshHeaderBubble() {
    return fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`)
      .then((response) => response.text())
      .then((text) => {
        const bubble = document.getElementById('cart-icon-bubble');
        if (!bubble) return;
        const html = new DOMParser().parseFromString(text, 'text/html');
        const newContent = html.querySelector('.shopify-section');
        if (newContent) bubble.innerHTML = newContent.innerHTML;
      });
  },

  announceVariants(drawerEl) {
    const ids = Array.from(drawerEl.querySelectorAll('[data-quantity-variant-id]')).map(
      (el) => el.dataset.quantityVariantId
    );
    document.dispatchEvent(new CustomEvent('velour:cart:updated', { detail: { variantIds: ids } } ));
  },
};
